export const RADAR_ATTENTION_DURATION_PATH = "/api/market-radar/attention-duration/v1" as const;
export const RADAR_ATTENTION_DURATION_MAX_BODY_BYTES = 32_768;
export const RADAR_ATTENTION_DURATION_SCHEMA = "radar-attention-duration-v1" as const;

export type RadarAttentionDurationGroup = {
  status: "available" | "insufficient_retained_history";
  candidate: number;
  complete: number;
  censored: number;
  excluded: number;
  unknown: number;
  uniqueSymbols: number;
  repeatedEpisodeExcess: number;
  completeDurationMs: { n: number; min: number; median: number; max: number } | null;
  symbolEpisodeCountHistogram: Record<string, number>;
  dispositionReasonCounts: Record<string, number>;
  censoring: {
    censorTypeCounts: Record<string, number>;
    observedLowerBoundCount: number;
    lowerBoundUnavailableReasonCounts: Record<string, number>;
    firstBreakCauseStatus: "verified_from_corrected_ledger_and_269_case_reconciliation";
    lowerBoundByCensorType: Record<string, { n: number; observedLowerBoundCount: number; unavailableReasonCounts: Record<string, number> }>;
    openAtStopBreakCauseCountsByDisposition: Record<string, Record<string, number>>;
    firstBreakCauseCountsByCensorType: Record<string, Record<string, number>>;
    unknownLowerBoundUnavailableReasonCounts: Record<string, number>;
  };
};

export type RadarAttentionDurationPacketV1 = {
  schema: typeof RADAR_ATTENTION_DURATION_SCHEMA;
  packetId: string;
  status: "historical_snapshot";
  source: "frozen_e12_population_packet";
  measure: {
    label: string;
    evaluationScheduleMs: number;
    retainedConfirmationResolutionMs: number;
    durationDefinition: string;
    summaryMethod: string;
    notClaims: string[];
  };
  population: {
    kind: "one_fixed_historical_invocation";
    invocationId: string;
    epochStartUtcInclusive: string;
    epochEndUtcExclusive: string;
    matureContextStartUtc: string;
    sourceCodeCommit: string;
    protectedEnvironmentSha256: string;
    startupSettingsObserved: { scanPollIntervalMs: number; watchSize: number; deepSize: number };
    fullHistoricalProcessEnvironmentRetained: false;
    fullResolvedNumericConfigurationCertified: false;
    crossEpochPooling: false;
    campaignStartingBaselineCommit: string;
  };
  provenance: {
    sourceDatabaseSha256: string;
    methodologySha256: string;
    sourceExtractionSha256: string;
    correctedLedgerSha256: string;
    independentFeasibilityReview: string;
    independentReviewVerdict: string;
    reviewScopeLimit: string;
    auditInputBundleSha256: string;
    auditManifestSha256: string;
  };
  coverage: {
    sourceRows: Record<string, number>;
    eligibilityTransitions: { toIneligible: number; toEligible: number };
    history1m: { validRows: number; invalidRows: number; missingBucketKeys: number };
    raw1m: { validKeys: number; invalidKeys: number; invalidReason: string };
    assignmentDenominator: { lifecycleCandidates: number; tierCandidates: number; totalCandidates: number; uniqueSymbolsLifecycle: number; uniqueSymbolsTier: number; uniqueSymbolsCombined: number };
    allCandidatesByDisposition: Record<string, number>;
    leftCensored: { episodes: number; observedLowerBoundAvailable: number; noMatureClippedInterval: number; noPositiveSampleBeforeFirstBreak: number; lowerBoundUnavailableReasons: Record<string, number> };
    openAtStop: { episodes: number; censored: number; unknown: number; excluded: number; firstBreakBySourceDispositionReason: Record<string, number> };
    limitations: string[];
    dispositionReasonCounts: { lifecycle: Record<string, number>; tier: Record<string, number> };
  };
  totals: {
    lifecycle: { candidate: number; complete: number; censored: number; excluded: number; unknown: number; uniqueSymbols: number };
    tier: { candidate: number; complete: number; censored: number; excluded: number; unknown: number; uniqueSymbols: number };
    combined: { candidate: number; complete: number; censored: number; excluded: number; unknown: number; uniqueSymbols: number; episodesAreIndependent: false };
  };
  groups: {
    lifecycle: Record<string, RadarAttentionDurationGroup>;
    tier: Record<string, RadarAttentionDurationGroup>;
  };
  limitations: string[];
};
